// Terminal form: shows one [data-step] at a time. A step commits on Enter (Ctrl/Cmd+Enter in a
// textarea), its [data-step-next] control, or blur when its field is valid; it then collapses into a
// mono [data-step-readout] line that scrambles in (click it to edit) and the next open step activates.
// Optional fields left blank commit as data-skipped. [data-terminal-submit] shows once the last step
// is reached. Server-side errors reopen the first invalid step. Without JS all fields stay visible.
import { scrambleTo } from '@theme/signal-glitch';

// Noise characters for the readout glitch: numbers only.
const DIGITS = '0123456789';
const ROLL_MS = 420; // How long each new character spins before it lands.
const ROLL_FRAME_MS = 40; // One digit step on the reel.

class STerminalForm extends HTMLElement {
  // Wait until the whole form (all four lines) is on the page before setting it up. Starting earlier caught
  // only the first lines (name, email), so phone and message never got their live print.
  connectedCallback() {
    if (this.started) return;
    const go = () => {
      if (this.started || !this.isConnected) return;
      this.started = true;
      this.setup();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go, { once: true });
    else requestAnimationFrame(go);
  }

  setup() {
    this.steps = [...this.querySelectorAll('[data-step]')];
    if (!this.steps.length) return;

    for (const step of this.steps) {
      const field = this.fieldOf(step);
      if (!field) continue;
      field.addEventListener('keydown', (event) => this.onKeydown(event, step));
      field.addEventListener('blur', () => this.onBlur(step));
      field.addEventListener('input', () => {
        // Typing in any box makes it the open line, so it prints live from the first letter.
        if (step.dataset.state !== 'active') this.edit(step);
        this.printLive(step);
        this.updateSubmit();
      });
      field.addEventListener('focus', () => {
        // Clicking or tapping into any box makes it the open line (every box is on screen at once).
        if (step.dataset.state !== 'active') this.edit(step);
        this.printLive(step);
        this.watch(step);
      });
      field.addEventListener('keyup', () => { if (step.dataset.state === 'active') this.printLive(step); });
      // Phones: once, when a box is tapped and the keyboard has opened, bring the line up so it sits above the
      // keyboard. Never while typing: the phone follows the cursor itself, and scrolling on every letter fought it,
      // sending the screen up and down.
      field.addEventListener('focus', () => setTimeout(() => this.keepInView(step), 450));
      field.addEventListener('change', () => { if (document.activeElement !== field) this.commit(step, false); });
      step.querySelector('[data-step-next]')?.addEventListener('click', () => this.commit(step, true));
      step.querySelector('[data-step-readout]')?.addEventListener('click', () => this.edit(step));
    }

    // Prefilled valid values (returning customers, re-rendered forms) start as readouts.
    for (const step of this.steps) {
      const field = this.fieldOf(step);
      const hasServerError = field?.getAttribute('aria-invalid') === 'true';
      if (field && field.value.trim() && field.checkValidity() && !hasServerError) {
        this.setDone(step, false);
      } else {
        step.dataset.state = 'pending';
      }
    }

    // Phones: iPhones don't leave a field when you tap outside it, and the number pad has no Enter key, so a
    // line (phone, message) could stay open. A real tap (not a scroll) anywhere outside the active step
    // finishes it, like Enter, and prints its value back as the readout line.
    this.outside = new AbortController();
    const { signal } = this.outside;
    let down = null;
    document.addEventListener('pointerdown', (event) => { down = { x: event.clientX, y: event.clientY }; }, { signal });
    document.addEventListener('pointercancel', () => { down = null; }, { signal });
    document.addEventListener('pointerup', (event) => {
      const start = down;
      down = null;
      if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
      const active = this.steps.find((step) => step.dataset.state === 'active');
      if (!active || active.contains(event.target)) return;
      const field = this.fieldOf(active);
      // Fill-in fields finish once they have text; the optional phone line also finishes (skipped) when blank.
      if (field && (field.value.trim() || !field.required)) {
        this.commit(active, false);
        field.blur();
      }
    }, { signal });

    this.dataset.ready = '';
    this.activateNext(false);
  }

  disconnectedCallback() {
    this.outside?.abort();
  }

  fieldOf(step) {
    return step.querySelector('input, textarea');
  }

  onKeydown(event, step) {
    if (event.key !== 'Enter' || event.isComposing) return;
    const isTextarea = event.currentTarget instanceof HTMLTextAreaElement;
    if (isTextarea && !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    if (isTextarea && this.commit(step, true)) {
      this.closest('form')?.requestSubmit();
      return;
    }
    this.commit(step, true);
  }

  onBlur(step) {
    // Let clicks on this step's own controls run first.
    requestAnimationFrame(() => {
      if (step.dataset.state !== 'active' || step.contains(document.activeElement)) return;
      this.commit(step, false);
    });
  }

  // Returns true when the step committed. `explicit` shows validation messages on failure.
  commit(step, explicit) {
    const field = this.fieldOf(step);
    if (!field) return true;
    // Already done: make sure the readout shows the current value (e.g. a number typed after a skip).
    if (step.dataset.state === 'done') {
      this.refreshReadout(step);
      return true;
    }
    const value = field.value.trim();

    if (!value && !field.required) {
      this.setDone(step, true);
      this.activateNext(explicit);
      return true;
    }
    if (!value || !field.checkValidity()) {
      if (explicit) field.reportValidity();
      return false;
    }
    field.removeAttribute('aria-invalid');
    this.setDone(step, true);
    this.activateNext(explicit);
    return true;
  }

  setDone(step, animate) {
    const field = this.fieldOf(step);
    const output = step.querySelector('[data-readout-value]');
    const value = field?.value.trim() || '';
    step.dataset.state = 'done';
    this.stopLive(step);
    if (!output) return;
    const text = value || this.dataset.skipped || '';
    output.classList.toggle('text-on-surface-secondary', !value);
    // Already printed live while typing: just settle it. Otherwise decode it in through digits.
    const printed = (this.live?.get(step)?.target || '').trim();
    if (printed && printed === text) {
      output.textContent = text;
      return;
    }
    if (animate) scrambleTo(output, text, 520, DIGITS);
    else output.textContent = text;
  }

  // While a line is open, its readout prints underneath as you type, from the first letter. Each new
  // character spins like a slot-machine reel, counting up through the digits, then lands on the real
  // letter. Every character keeps its own reel, so typing fast leaves a row of spinning numbers behind.
  // Each line gets its own live print line, shown with inline styles so no page style can hide it.
  liveLine(step) {
    let line = step.querySelector('[data-live-line]');
    if (line) return line;
    const label = step.querySelector('[data-step-readout] > span')?.textContent || '';
    line = document.createElement('div');
    line.setAttribute('data-live-line', '');
    line.setAttribute('aria-hidden', 'true');
    line.className = 'font-heading text-accent';
    line.style.cssText = 'display:none;overflow-wrap:anywhere;text-align:start;';
    const prefix = document.createElement('span');
    prefix.className = 'text-on-surface-secondary';
    prefix.textContent = `${label} `;
    const value = document.createElement('span');
    value.setAttribute('data-live-value', '');
    value.style.whiteSpace = 'pre-wrap';
    const cursor = document.createElement('span');
    cursor.className = 'terminal-live-cursor';
    cursor.textContent = '█';
    cursor.style.marginInlineStart = '0.1em';
    cursor.style.animation = 'signal-blink 1s steps(1) infinite';
    const fieldBox = step.querySelector('.terminal-step-field');
    if (step.querySelector('textarea')) {
      // Message: a fixed two-line window above the box, always showing the latest words. It keeps its height
      // from the moment the line opens, so the box never moves while you type.
      const inner = document.createElement('div');
      inner.append(prefix, value, cursor);
      line.append(inner);
      line.dataset.window = '';
      line.style.cssText += 'height:2lh;overflow:hidden;flex-direction:column;justify-content:flex-end;';
      fieldBox.before(line);
      return line;
    }
    line.append(prefix, value, cursor);
    // The other lines print underneath their box.
    fieldBox.after(line);
    return line;
  }

  printLive(step) {
    const field = this.fieldOf(step);
    if (!field) return;
    const line = this.liveLine(step);
    const output = line.querySelector('[data-live-value]');
    this.live ??= new WeakMap();
    const state = this.live.get(step) || { target: '', born: [], timer: null };
    const target = field.value;
    const now = performance.now();
    let common = 0;
    while (common < target.length && common < state.target.length && target[common] === state.target[common]) common++;
    state.born = state.born.slice(0, common);
    for (let i = common; i < target.length; i++) state.born[i] = now;
    state.target = target;
    this.live.set(step, state);
    const open = step.dataset.state === 'active';
    if ('window' in line.dataset) {
      // The message window holds its place the whole time the line is open; it is just blank until you type.
      line.style.display = open ? 'flex' : 'none';
      line.style.visibility = target.trim() ? 'visible' : 'hidden';
    } else {
      line.style.display = open && target.trim() ? 'block' : 'none';
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      state.born.fill(-Infinity);
      output.textContent = target;
      return;
    }
    const render = () => {
      const time = performance.now();
      let rolling = false;
      let text = '';
      for (let i = 0; i < state.target.length; i++) {
        const char = state.target[i];
        const age = time - state.born[i];
        if (/\s/.test(char) || age >= ROLL_MS) {
          text += char;
        } else {
          rolling = true;
          // Reel: counts up one digit per frame from a different start for each position.
          text += DIGITS[(i * 7 + Math.floor(age / ROLL_FRAME_MS)) % DIGITS.length];
        }
      }
      output.textContent = text;
      return rolling;
    };
    if (state.timer) return;
    const tick = () => {
      state.timer = render() ? setTimeout(tick, ROLL_FRAME_MS) : null;
    };
    tick();
  }

  stopLive(step) {
    const line = step.querySelector('[data-live-line]');
    if (line) line.style.display = 'none';
    const state = this.live?.get(step);
    if (!state) return;
    clearTimeout(state.timer);
    state.timer = null;
    state.born.fill(-Infinity);
  }

  refreshReadout(step) {
    const field = this.fieldOf(step);
    const output = step.querySelector('[data-readout-value]');
    if (!field || !output) return;
    const text = field.value.trim() || this.dataset.skipped || '';
    if (output.textContent !== text) this.setDone(step, true);
  }

  edit(step) {
    for (const other of this.steps) {
      if (other !== step && other.dataset.state === 'active') {
        const field = this.fieldOf(other);
        if (field && field.value.trim() && field.checkValidity()) this.setDone(other, false);
        else if (field && !field.required) this.setDone(other, false);
        else other.dataset.state = 'pending';
      }
    }
    step.dataset.state = 'active';
    this.printLive(step);
    this.watch(step);
    this.updateSubmit();
    this.fieldOf(step)?.focus();
  }

  activateNext(focus) {
    const next = this.steps.find((step) => step.dataset.state !== 'done');
    if (next) {
      next.dataset.state = 'active';
      // Start the live print right away, the same way reopening a line does, so it runs from the first letter.
      this.printLive(next);
      this.watch(next);
      if (focus) this.fieldOf(next)?.focus();
    }
    this.updateSubmit();
  }

  keepInView(step) {
    if (step.dataset.state !== 'active' || !step.contains(document.activeElement)) return;
    const viewport = window.visualViewport;
    const top = viewport ? viewport.offsetTop : 0;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const header = document.querySelector('header')?.getBoundingClientRect().bottom || 0;
    const rect = step.getBoundingClientRect();
    const visibleTop = Math.max(top, header) + 12;
    // A line taller than the space above the keyboard (the message) can never fully fit: bring the start of the
    // message (its live print, else its box) just under the header once, then leave the page still while typing,
    // instead of scrolling on every letter and fighting the phone's own scroll to the caret.
    if (rect.height + 24 > bottom - visibleTop) {
      const anchor = [step.querySelector('[data-live-line]'), step.querySelector('.terminal-step-field')]
        .find((el) => el && el.offsetHeight);
      const at = anchor.getBoundingClientRect().top;
      if (at >= visibleTop - 4 && at <= visibleTop + (bottom - visibleTop) * 0.35) return;
      window.scrollBy({ top: at - visibleTop, behavior: 'instant' });
      return;
    }
    if (rect.bottom + 12 <= bottom && rect.top >= visibleTop) return;
    // Bring the line up so it starts just under the header; everything under it stays above the keyboard.
    window.scrollBy({ top: rect.top - visibleTop, behavior: 'instant' });
  }

  // While a line is open, keep checking its text, so every letter prints live even when the keyboard
  // doesn't report the keystroke (phone autocorrect, predictions, dictation).
  watch(step) {
    clearInterval(this.watcher);
    const field = this.fieldOf(step);
    if (!field) return;
    this.watcher = setInterval(() => {
      if (step.dataset.state !== 'active') {
        clearInterval(this.watcher);
        return;
      }
      if (field.value !== (this.live?.get(step)?.target ?? '')) {
        this.printLive(step);
        this.updateSubmit();
      }
    }, 50);
  }

  // Send appears only once every field is filled in: name and email valid, the optional phone line finished
  // (or skipped), and a message typed. Re-checked on every keystroke.
  updateSubmit() {
    const complete = this.steps.every((step) => {
      const field = this.fieldOf(step);
      if (!field) return true;
      if (!field.required) return step.dataset.state !== 'pending' && (!field.value.trim() || field.checkValidity());
      return Boolean(field.value.trim()) && field.checkValidity();
    });
    this.toggleAttribute('data-complete', complete);
  }
}

if (!customElements.get('s-terminal-form')) customElements.define('s-terminal-form', STerminalForm);

// Sent: the confirmation box arrives through the add-to-cart digit transfer. On submit we remember where the
// Send button was; after the page comes back with the confirmation, the digits stream from that spot into
// the box (s-cart-rain.js) and it draws in. Without the effect (reduced motion, no cart rain), it just shows.
const ORIGIN_KEY = 'contact-send-origin';
document.addEventListener('submit', (event) => {
  if (!event.target.closest?.('s-terminal-form, form')?.querySelector('s-terminal-form')) return;
  const rect = (event.submitter || event.target.querySelector('[type="submit"]'))?.getBoundingClientRect();
  if (!rect) return;
  try {
    sessionStorage.setItem(ORIGIN_KEY, JSON.stringify({ left: rect.left, top: rect.top, width: rect.width, height: rect.height }));
  } catch {}
}, { capture: true });

const sentBox = document.querySelector('[data-signal-reveal]');
if (sentBox) {
  const reveal = () => { sentBox.dataset.revealed = ''; };
  try { sessionStorage.removeItem(ORIGIN_KEY); } catch {}
  // Back to the top of the page (the CONTACT heading). The store returns here with "?contact_posted=true#contact_form",
  // and the browser jumps to that #contact_form anchor after load, so: drop the flag and the anchor from the address
  // (a refresh then shows the plain contact page, without the confirmation) and scroll to the top again once the
  // page has fully loaded, after the browser's own jump.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  history.replaceState(history.state, '', window.location.pathname);
  const toTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  toTop();
  sentBox.focus({ preventScroll: true });
  if (document.readyState !== 'complete') window.addEventListener('load', () => { toTop(); requestAnimationFrame(toTop); }, { once: true });
  else requestAnimationFrame(toTop);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !document.querySelector('s-cart-rain')) {
    reveal();
  } else {
    const fallback = setTimeout(reveal, 4000);
    // Start once the page has loaded and is back at the top, so the stream lines up with the box.
    const loaded = document.readyState === 'complete' ? Promise.resolve() : new Promise((resolve) => window.addEventListener('load', resolve, { once: true }));
    Promise.all([customElements.whenDefined('s-cart-rain'), loaded]).then(() => {
      toTop();
      clearTimeout(fallback);
      requestAnimationFrame(() => {
        // The digits gather just above the box (centred over it) and pour down into it.
        const box = sentBox.getBoundingClientRect();
        const lift = Math.min(140, Math.max(48, box.top - 8));
        const origin = { left: box.left + box.width / 2 - 1, top: Math.max(0, box.top - lift), width: 2, height: 2 };
        document.dispatchEvent(new CustomEvent('s-signal:transfer', { detail: { target: sentBox, origin } }));
        setTimeout(reveal, 1350);
      });
    });
  }
}
